import { FC, useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Typography,
  CircularProgress,
} from '@mui/material'
import { IPlaybookReport } from '../../../../../shared/types/api/playbook.type'
import { runPlaybookReport } from '../../../../services/playbook-report.service'

interface IProps {
  open: boolean
  report: IPlaybookReport
  playbookSlug: string
  onClose: () => void
}

const PlaybookRunReportModal: FC<IProps> = ({ open, report, playbookSlug, onClose }) => {
  const [paramValues, setParamValues] = useState<Record<string, string>>({})
  const [rows, setRows] = useState<Record<string, unknown>[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const initial: Record<string, string> = {}
    for (const p of report.params ?? []) {
      initial[p] = ''
    }
    setParamValues(initial)
    setRows(null)
    setError(null)
  }, [report, open])

  const handleRun = async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await runPlaybookReport(playbookSlug, report.slug, paramValues)
      setRows(Array.isArray(result) ? result.slice(0, 20) : [result])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to run report')
    } finally {
      setLoading(false)
    }
  }

  const columns = rows && rows.length > 0 ? Object.keys(rows[0]) : []

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Run: {report.name}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
        {report.params?.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {report.params.map((param) => (
              <TextField
                key={param}
                label={param}
                value={paramValues[param] ?? ''}
                onChange={(e) => setParamValues((prev) => ({ ...prev, [param]: e.target.value }))}
                size="small"
                fullWidth
                data-testid={`run-report-param-${param}`}
                inputProps={{ 'data-testid': `run-report-param-input-${param}` }}
              />
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} data-testid="run-report-cancel-button">
          Cancel
        </Button>
        <Button onClick={handleRun} variant="contained" disabled={loading} data-testid="run-report-run-button">
          {loading ? <CircularProgress size={20} /> : 'Run'}
        </Button>
      </DialogActions>
      {error && (
        <Box sx={{ px: 3, pb: 2 }}>
          <Typography color="error" variant="body2" data-testid="run-report-error">
            {error}
          </Typography>
        </Box>
      )}
      {rows && (
        <Box sx={{ px: 3, pb: 2 }}>
          {rows.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No results
            </Typography>
          ) : (
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {columns.map((col) => (
                      <TableCell key={col} sx={{ fontWeight: 600 }}>
                        {col}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row, i) => (
                    <TableRow key={i}>
                      {columns.map((col) => (
                        <TableCell key={col}>
                          {row[col] !== null && typeof row[col] === 'object'
                            ? JSON.stringify(row[col])
                            : String(row[col] ?? '')}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}
    </Dialog>
  )
}

export default PlaybookRunReportModal
